import i18n, { getLocalizedConsumptionTaxName } from '@/utils/i18n';
import React from 'react';
import {
  displayCurrencyPlaceholder,
  formatCurrency,
  OrderFee,
} from '@/utils/money';
import { OrderFeeDisplayType, ValidateOrderFieldsFragment } from '@/types';
import { OrderFeeName } from '@/enums';
import { StyleSheet, Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { TYPOGRAPHY } from '@/styles';
import { useUserContext } from '@/contexts/user-context';

const VISIBLE_DISPLAY_TYPES = new Set([
  OrderFeeDisplayType.Subtotal,
  OrderFeeDisplayType.Fee,
]);

const NO_SUBTOTAL_DISPLAY_TYPES = new Set([OrderFeeDisplayType.Fee]);

interface Props {
  currencyCode: string | null;
  includeSubtotal: boolean;
  isUpdating: boolean;
  order: ValidateOrderFieldsFragment;
  orderFees: OrderFee[];
}

export const OrderSummaryFees: React.FC<Props> = ({
  currencyCode,
  isUpdating,
  order,
  orderFees,
  includeSubtotal,
}) => {
  const {
    selectedRestaurantRelationship: { consumptionTax },
  } = useUserContext();

  const consumptionTaxName = consumptionTax
    ? getLocalizedConsumptionTaxName(consumptionTax[0].name)
    : null;

  const visibleTypes = includeSubtotal
    ? VISIBLE_DISPLAY_TYPES
    : NO_SUBTOTAL_DISPLAY_TYPES;
  const unitCount = order.orders_items.reduce(
    (result, oi) =>
      result + (oi.order_item_prices?.pack_quantity_at_order ?? 0),
    0,
  );
  const unitCountText = i18n.t(
    unitCount === 1 ? 'unit_count' : 'unit_count_plural',
    { count: isUpdating ? '--' : unitCount },
  );

  return (
    <>
      {orderFees.reduce<React.ReactNode[]>((result, fee, index) => {
        const amountInMicros =
          fee.amount_at_invoice_micros ?? fee.amount_at_order_micros;

        // The backend may provide us with tax, or consumption tax. As long as
        // consumption tax is enabled for the supplier, use that display name.
        const isTax = fee.name === OrderFeeName.TAX;
        const isConsumptionTax = consumptionTax
          ? fee.name === consumptionTax[0].name
          : false;
        const feeName =
          (isTax || isConsumptionTax) && consumptionTaxName
            ? consumptionTaxName
            : fee.name;

        if (
          visibleTypes.has(fee.display_type as OrderFeeDisplayType) &&
          typeof amountInMicros === 'number'
        ) {
          result.push(
            <View key={index} style={styles.row}>
              <View style={styles.nameContainer}>
                <Typography variant="paragraph-md">{feeName}</Typography>
                {fee.display_type === OrderFeeDisplayType.Subtotal ? (
                  <Text style={TYPOGRAPHY.p}> ({unitCountText})</Text>
                ) : null}
              </View>
              <Text style={TYPOGRAPHY.p}>
                {isUpdating
                  ? displayCurrencyPlaceholder(currencyCode)
                  : formatCurrency(amountInMicros, currencyCode)}
              </Text>
            </View>,
          );
        }

        return result;
      }, [])}
    </>
  );
};

const styles = StyleSheet.create({
  nameContainer: {
    flexDirection: 'row',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
});
